import { t } from "@/i18n-js/instance";
import { isTrialThenValue } from "@/react/helpers/paywallPriceHelpers";
import type { PaywallCheckoutPreview } from "@circle-react/types";
import type { CheckoutPreviewEntryProps } from "../../../components/PreviewEntry";
import type { ComponentLineProps } from "../../FirstPaymentPreview/types";
import { shouldExpandFirstPayment } from "../../FirstPaymentPreview/variants/helpers";
import { RawLines } from "../RawLines";
import { getNumberOfInstallments } from "../variants/helpers";
import { shouldExpandFuturePayments } from "../variants/helpers";

const trialSubtotal = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => ({
  valueWeight: shouldExpandFuturePayments(preview) ? "normal" : "semibold",
  value: preview.future_payments?.formatted_amount_due,
  label: t("paywall_checkout.future_payments.one_time_payment_on", {
    date: preview.future_payments?.formatted_start_on,
  }),
});

const subtotal = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => ({
  value: preview.future_payments?.formatted_amount_due,
  label: t("paywall_checkout.future_payments.one_time_payment_on", {
    date: preview.future_payments?.formatted_start_on,
  }),
  valueWeight: !shouldExpandFuturePayments(preview) ? "semibold" : "normal",
});

const oneInstallmentStrategies = {
  trial_then_value: (
    preview: PaywallCheckoutPreview,
  ): Partial<CheckoutPreviewEntryProps> => ({
    valueWeight: shouldExpandFirstPayment(preview) ? "normal" : "semibold",
    value: preview.first_payment?.formatted_amount_due,
    label: t("paywall_checkout.future_payments.one_time_payment_on", {
      date: preview.first_payment?.formatted_due_on,
    }),
  }),
  trial_then_upfront_then_value: trialSubtotal,
  upfront_then_trial_then_value: subtotal,
  upfront_then_value: subtotal,
  value: () => ({ hide: true }),
};

const multipleInstallmentsSubtotal = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => {
  if (isTrialThenValue(preview.preview_scenario)) {
    return {
      value: preview.first_payment?.formatted_amount_due,
      valueWeight: !shouldExpandFirstPayment(preview) ? "semibold" : "normal",
      label: t(
        [
          "paywall_checkout.future_payments.next_payment_on_v2",
          preview.paywall_price.interval_type,
        ],
        {
          date: preview?.first_payment?.formatted_due_on,
        },
      ),
    };
  }

  return {
    value: preview.future_payments?.formatted_amount_due,
    valueWeight: !shouldExpandFuturePayments(preview) ? "semibold" : "normal",
    label: t(
      [
        "paywall_checkout.future_payments.next_payment_on_v2",
        preview.paywall_price.interval_type,
      ],
      {
        date: preview.future_payments?.formatted_start_on,
      },
    ),
  };
};

const buildOptions = (
  preview: PaywallCheckoutPreview,
): Partial<CheckoutPreviewEntryProps> => {
  if (getNumberOfInstallments(preview) === 1) {
    return oneInstallmentStrategies[preview.preview_scenario](preview);
  }
  return multipleInstallmentsSubtotal(preview);
};

export const SubtotalLine = ({
  preview,
}: Pick<ComponentLineProps, "preview">) => {
  const options = buildOptions(preview);
  return <RawLines.SubtotalLine preview={preview} options={options} />;
};
